import { useState } from "react";
import { Button } from "../../components/ui";

export default function ThresholdEditView({ target, onBack, onSave }) {
  const isAlert = target.kind === "alert";
  const existing = target.existing;

  const [isPercent, setIsPercent] = useState(
    isAlert ? existing?.alert_type !== "absolute" : existing?.cutoff_type !== "absolute"
  );
  const initialValue = isAlert ? existing?.alert_value : existing?.cutoff_value;
  const [valueText, setValueText] = useState(
    initialValue && initialValue > 0
      ? Number.isInteger(initialValue)
        ? String(initialValue)
        : String(initialValue)
      : ""
  );
  const [cutoffEnabled, setCutoffEnabled] = useState(existing?.cutoff_active === true);

  const hasExistingValue = isAlert ? (existing?.alert_value ?? 0) > 0 : (existing?.cutoff_value ?? 0) > 0;
  const numericValue = parseFloat(valueText);
  const canSave = !Number.isNaN(numericValue) && numericValue > 0;

  function handleValueChange(e) {
    const filtered = e.target.value.replace(/[^0-9.]/g, "");
    setValueText(filtered);
  }

  function remove() {
    if (isAlert) onSave({ alert_type: null, alert_value: 0, cutoff_type: null, cutoff_value: 0, cutoff_active: false });
    else onSave({ alert_type: null, alert_value: 0, cutoff_type: null, cutoff_value: 0, cutoff_active: false });
  }

  function save() {
    const type = isPercent ? "percent" : "absolute";
    if (isAlert) {
      onSave({ alert_type: type, alert_value: numericValue, cutoff_type: null, cutoff_value: 0, cutoff_active: false });
    } else {
      onSave({ alert_type: null, alert_value: 0, cutoff_type: type, cutoff_value: numericValue, cutoff_active: cutoffEnabled });
    }
  }

  return (
    <div className="tg-subpage">
      <div className="tg-subpage__header">
        <button className="tg-icon-btn" onClick={onBack} aria-label="Back">
          <BackIcon />
        </button>
        <div>
          <h2 style={{ fontSize: 17 }}>{isAlert ? "Token alert" : "Auto cutoff"}</h2>
          <p style={{ fontSize: 12, color: "var(--slate)" }}>{target.llmLabel}</p>
        </div>
      </div>

      <div className="tg-subpage__body">
        <p style={{ fontSize: 13.5, color: "var(--slate)" }}>
          {isAlert
            ? "Get notified when usage for this URL crosses a limit you choose."
            : "Automatically block further requests to this URL once usage hits the limit. You can reactivate it any time from Monitor."}
        </p>

        {!isAlert && (
          <div className="tg-switch-row" style={{ marginTop: 20 }}>
            <div>
              <p style={{ fontSize: 13.5, fontWeight: 500 }}>Enable auto cutoff</p>
              <p style={{ fontSize: 11.5, color: "var(--slate)" }}>Off keeps the limit saved without enforcing it.</p>
            </div>
            <Switch checked={cutoffEnabled} onChange={setCutoffEnabled} />
          </div>
        )}

        <p style={{ fontSize: 12.5, fontWeight: 600, marginTop: 20, marginBottom: 8 }}>Limit type</p>
        <div style={{ display: "flex", gap: 8 }}>
          <FilterChip selected={isPercent} onClick={() => setIsPercent(true)}>
            Percent
          </FilterChip>
          <FilterChip selected={!isPercent} onClick={() => setIsPercent(false)}>
            Absolute
          </FilterChip>
        </div>

        <div style={{ marginTop: 16, position: "relative" }}>
          <label className="tg-field">
            <span className="tg-field__label">{isPercent ? "Threshold, e.g. 80" : "Tokens, e.g. 800000"}</span>
            <span className="tg-field__control">
              <input className="tg-field__input" value={valueText} onChange={handleValueChange} inputMode="decimal" />
              {isPercent && <span className="tg-field__suffix">%</span>}
            </span>
          </label>
        </div>
      </div>

      <div className="tg-subpage__footer">
        {hasExistingValue && (
          <Button variant="outlined" tone="error" fullWidth onClick={remove}>
            {isAlert ? "Remove alert" : "Remove cutoff"}
          </Button>
        )}
        <Button fullWidth disabled={!canSave} onClick={save}>
          {hasExistingValue ? "Update" : "Save"}
        </Button>
      </div>
    </div>
  );
}

function Switch({ checked, onChange }) {
  return (
    <button
      className={`tg-switch ${checked ? "tg-switch--on" : ""}`}
      onClick={() => onChange(!checked)}
      role="switch"
      aria-checked={checked}
    >
      <span className="tg-switch__knob" />
    </button>
  );
}

function FilterChip({ selected, onClick, children }) {
  return (
    <button className={`tg-filterchip ${selected ? "tg-filterchip--selected" : ""}`} onClick={onClick}>
      {children}
    </button>
  );
}

function BackIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
